import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { useApplicationContext } from "../../application/ApplicationContext";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { ProgressSpinner } from "primereact/progressspinner";
import { RoleType } from "../../models/Role";
import { ProcessingLogListItem } from "../../models/ProcessingLog";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { activeUser } from "../../services/AuthService";
import { Utility } from "../../core/Utility";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useTranslation } from "react-i18next";
import { AxiosResponse, AxiosError } from "axios";
import {
  deleteOneProcessingLog,
  getProcessingLogs,
} from "../../services/FinishingCenterService";
import "./ProcessingLogsPage.scss";

export const ProcessingLogsPage = () => {
  const {
    //load and set global filters
    state,
    selectedDateFromSet,
    selectedDateToSet,
  } = useApplicationContext();

  const { t } = useTranslation();
  const toast = useRef(null);
  const navigate = useNavigate();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const today = new Date();
  const isCurrentBusinessYear = businessYear === today.getFullYear();
  const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31, 23, 59, 59);
  const initialDateFrom = isCurrentBusinessYear
    ? new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 30,
        0,
        0,
        1,
      )
    : new Date(businessYear, 0, 1, 0, 0, 1); // 1.1. poslovne godine
  const initialDateTo = isCurrentBusinessYear
    ? today
    : new Date(businessYear, 11, 31, 23, 59, 59); // 31.12. poslovne godine
  const [dateFrom, setDateFrom] = useState<Date>(
    state.selectedDateFrom ?? initialDateFrom,
  );
  const [dateTo, setDateTo] = useState<Date>(
    state.selectedDateTo ?? initialDateTo,
  );

  const [allProcessingLogs, setAllProcessingLogs] = useState<
    ProcessingLogListItem[]
  >([]);
  const [filteredInputDocuments, setFilteredInputDocuments] = useState<
    ProcessingLogListItem[]
  >([]);

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [inputToDelete, setInputToDelete] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState({
    number: { value: null, matchMode: FilterMatchMode.CONTAINS },
    producedAmountKg: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  useEffect(() => {
    setProcessing(true);
    if (state.selectedDateFrom) setDateFrom(state?.selectedDateFrom);
    if (state.selectedDateTo) setDateTo(state?.selectedDateTo);

    const effectiveFrom = state?.selectedDateFrom ?? initialDateFrom;
    const effectiveTo = state?.selectedDateTo ?? initialDateTo;

    getProcessingLogs(undefined, undefined, effectiveFrom, effectiveTo)
      .then((response: AxiosResponse) => {
        const allNewLogs = response.data.result;
        setAllProcessingLogs(allNewLogs);
      })
      .catch((error: AxiosError) => {
        console.log(error);
      })
      .finally(() => setProcessing(false));
  }, []);

  useEffect(() => {
    const today = new Date();
    const isCurrent = businessYear === today.getFullYear();

    const newFrom = isCurrent
      ? new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - 30,
          0,
          0,
          1,
        )
      : new Date(businessYear, 0, 1, 0, 0, 1);

    const newTo = isCurrent
      ? today
      : new Date(businessYear, 11, 31, 23, 59, 59);

    setDateFrom(newFrom);
    setDateTo(newTo);
    selectedDateFromSet(newFrom);
    selectedDateToSet(newTo);
  }, [businessYear]);

  const resetFilters = () => {
    setFilters({
      number: { value: null, matchMode: FilterMatchMode.CONTAINS },
      producedAmountKg: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  useEffect(() => {
    setProcessing(true);
    const effectiveFrom = state?.selectedDateFrom ?? initialDateFrom;
    const effectiveTo = state?.selectedDateTo ?? initialDateTo;

    getProcessingLogs(undefined, undefined, effectiveFrom, effectiveTo)
      .then((response: AxiosResponse) => {
        const allLogs = response.data.result;
        setAllProcessingLogs(allLogs);
        resetFilters();
      })
      .finally(() => setProcessing(false));
  }, [state.selectedDateFrom, state.selectedDateTo]);

  const onDateFromChange = (_property: string, value: Date | null) => {
    setDateFrom(value as Date);
    selectedDateFromSet(value as Date);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
    selectedDateToSet(value as Date);
  };

  const accept = () => {
    setProcessing(true);
    deleteOneProcessingLog(inputToDelete)
      .then(() => {
        setInputToDelete("");
      })
      .then(() => {
        const effectiveFrom = state?.selectedDateFrom ?? initialDateFrom;
        const effectiveTo = state?.selectedDateTo ?? initialDateTo;

        getProcessingLogs(
          undefined,
          undefined,
          effectiveFrom,
          effectiveTo,
        ).then((response: AxiosResponse) => {
          const allLogs = response.data.result;
          setAllProcessingLogs(allLogs);
          resetFilters();
        });
      })
      .finally(() => setProcessing(false));
    //@ts-ignore
    toast.current.show({
      severity: "success",
      detail: t(`Zapisnik je izbrisan`),
      life: 3000,
    });
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const formatDate = (rowData: ProcessingLogListItem) => {
    return Utility.formatDate(new Date(rowData?.date as Date));
  };

  const formatOrderNumber = (rowData: ProcessingLogListItem) => {
    return rowData?.order?.number ?? "";
  };

  const formatPlantType = (rowData: ProcessingLogListItem) => {
    return rowData?.order?.plantType ?? "";
  };

  const formatSortPlant = (rowData: ProcessingLogListItem) => {
    return rowData?.producedProduct?.name ?? "";
  };

  const formatOptions = (rowData: ProcessingLogListItem) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            const id = rowData.id;
            navigate(`/processing-logs/${id}`);
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Director) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-trash"
            onButtonClick={async () => {
              const id = rowData.id;
              setInputToDelete(id);
              setDeleteConfirmVisible(true);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
      </div>
    );
  };

  const exportToExcel = () => {
    const sourceLogs =
      filteredInputDocuments && filteredInputDocuments.length > 0
        ? filteredInputDocuments
        : allProcessingLogs;

    // sortiramo po datumu (od najstarijeg ka najnovijem)
    const sortedLogs = [...sourceLogs].sort((a, b) => {
      const dateA = a.date ? new Date(a.date as Date).getTime() : 0;
      const dateB = b.date ? new Date(b.date as Date).getTime() : 0;
      return dateA - dateB;
    });

    const COL_DATE = "Datum";
    const COL_LOG_NO = "Broj zapisnika";
    const COL_ORDER_NO = "Broj naloga za doradu";
    const COL_PLANT_TYPE = "Biljna vrsta (iz naloga)";
    const COL_SEED_CATEGORY = "Kategorija semena (iz naloga)";
    const COL_AMOUNT = "Količina semenske robe (iz naloga) (kg)";
    const COL_SEED_PRODUCED = "Vrsta proizvedenog semena";
    const COL_PRODUCED_AMOUNT = "Proizvedena količina semena (kg)";
    const COL_NATURAL_AMOUNT = "Utrošena količina naturalne robe (kg)";
    const COL_USEFUL = "Koristan otpad (kg)";
    const COL_USELESS = "Nekoristan otpad (kg)";
    const COL_RED = "Crveno zrno (kg)";
    const COL_REPRO_NAME = "Repromaterijal - naziv";
    const COL_REPRO_UOM = "Repromaterijal - JM";
    const COL_REPRO_QTY = "Repromaterijal - količina";
    const COL_REPRO_FOREIGN = "Repromaterijal - tuđa roba";
    const COL_REPRO_OWNER = "Repromaterijal - vlasnik";

    const excelRows: any[] = [];

    sortedLogs.forEach((log) => {
      const consumptions = (log as any).consumptions ?? [];

      const baseRowCommonData: any = {
        [COL_DATE]: log.date
          ? Utility.formatDate(new Date(log.date as Date))
          : "",
        [COL_LOG_NO]: log.number ?? "",
        [COL_ORDER_NO]: log.order?.number ?? "",
        [COL_PLANT_TYPE]: log.order?.plantType ?? "",
        [COL_SEED_CATEGORY]:
          (log.order as any)?.seedCategoryName ??
          (log.order as any)?.seedCategory ??
          "",
        [COL_AMOUNT]: Number(log?.order?.totalAmountKg) ?? "",
        [COL_SEED_PRODUCED]: log?.producedProduct?.name ?? "",
        [COL_PRODUCED_AMOUNT]: Number(log?.producedAmountKg) ?? "",
        [COL_NATURAL_AMOUNT]: Number(log?.naturalUsedKg) ?? "",
        [COL_USEFUL]:
          log.usefulWasteKg != null ? Number(log.usefulWasteKg) : "",
        [COL_USELESS]:
          log.uselessWasteKg != null ? Number(log.uselessWasteKg) : "",
        [COL_RED]: log.redGrainKg != null ? Number(log.redGrainKg) : "",
      };

      if (!consumptions || consumptions.length === 0) {
        excelRows.push({
          ...baseRowCommonData,
          [COL_REPRO_NAME]: "",
          [COL_REPRO_UOM]: "",
          [COL_REPRO_QTY]: "",
          [COL_REPRO_FOREIGN]: "",
          [COL_REPRO_OWNER]: "",
        });
      } else {
        consumptions.forEach((c: any, index: number) => {
          const isFirst = index === 0;

          const commonPart = isFirst
            ? baseRowCommonData // prvi – puni A–J
            : {
                // sledeći redovi – prazne A–J
                [COL_DATE]: "",
                [COL_LOG_NO]: "",
                [COL_ORDER_NO]: "",
                [COL_PLANT_TYPE]: "",
                [COL_SEED_CATEGORY]: "",
                [COL_AMOUNT]: "",
                [COL_SEED_PRODUCED]: "",
                [COL_PRODUCED_AMOUNT]: "",
                [COL_NATURAL_AMOUNT]: "",
                [COL_USEFUL]: "",
                [COL_USELESS]: "",
                [COL_RED]: "",
              };

          excelRows.push({
            ...commonPart,
            [COL_REPRO_NAME]: c.productName ?? "",
            [COL_REPRO_UOM]: c.uom ?? "",
            [COL_REPRO_QTY]: c.amount != null ? Number(c.amount) : "",
            [COL_REPRO_FOREIGN]: c.isForeign ? "DA" : "NE",
            [COL_REPRO_OWNER]: c.partnerName ?? "",
          });
        });
      }

      excelRows.push({
        [COL_DATE]: "",
        [COL_LOG_NO]: "",
        [COL_ORDER_NO]: "",
        [COL_PLANT_TYPE]: "",
        [COL_SEED_CATEGORY]: "",
        [COL_AMOUNT]: "",
        [COL_SEED_PRODUCED]: "",
        [COL_PRODUCED_AMOUNT]: "",
        [COL_NATURAL_AMOUNT]: "",
        [COL_USEFUL]: "",
        [COL_USELESS]: "",
        [COL_RED]: "",
        [COL_REPRO_NAME]: "",
        [COL_REPRO_UOM]: "",
        [COL_REPRO_QTY]: "",
        [COL_REPRO_FOREIGN]: "",
        [COL_REPRO_OWNER]: "",
      });
    });

    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelRows);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Zapisnici o finalnoj doradi semena ${Utility.formatDate(new Date())}`,
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-file header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">
              {t("Zapisnici o finalnoj doradi semena")}
            </span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const today = new Date();
                const isCurrent = businessYear === today.getFullYear();
                const newFrom = isCurrent
                  ? new Date(
                      today.getFullYear(),
                      today.getMonth(),
                      today.getDate() - 30,
                      0,
                      0,
                      1,
                    )
                  : new Date(businessYear, 0, 1, 0, 0, 1);
                const newTo = isCurrent
                  ? today
                  : new Date(businessYear, 11, 31, 23, 59, 59);
                // lokalni state
                setDateFrom(newFrom);
                setDateTo(newTo);
                // globalni filteri
                selectedDateFromSet(newFrom);
                selectedDateToSet(newTo);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem",
              }}
              labelText={t("Novi zapisnik")}
              icon="bi bi-file"
              onButtonClick={() => {
                navigate(`/processing-logs/new`);
              }}
              disabled={processing === true}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              emptyMessage="Nema zapisnika o finalnoj doradi"
              value={allProcessingLogs}
              onValueChange={(filteredData) =>
                setFilteredInputDocuments(filteredData)
              }
              showGridlines
              stripedRows
            >
              <Column
                field="number"
                filter
                header={t("Broj zapisnika")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
              />
              <Column
                body={formatOrderNumber}
                header={t("Nalog za doradu")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={formatPlantType}
                header={t("Biljna vrsta")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatSortPlant}
                header={t("Proizvedeno seme")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="producedAmountKg"
                filter
                style={{ minWidth: "80px" }}
                header={t("Proizvedena količina")}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "150px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t(
          "Da li ste sigurni da želite da izbrišete zapisnik o finalnoj doradi?",
        )}
        header={t("Potvrda brisanja zapisnika o finalnoj doradi semena")}
        icon="bi bi-exclamation-circle"
        acceptLabel={`${t("Da")}`}
        acceptClassName="accept-button"
        accept={accept}
        rejectLabel={`${t("Ne")}`}
        rejectClassName="reject-button"
        reject={() => {}}
      />
    </PageLayout>
  );
};
